import { Option } from '../../../common/components/FilterPanel/types';
import { InvalidUrlError } from '../../../common/errors/errors';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { SorterSet as GQLSorterSet } from '../../../common/types/gql-types';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { ReportCiphersuiteMapper } from '../mappers/ReportCiphersuiteMapper';
import { Ciphersuite } from '../models/Ciphersuite';

export const GET_REPORT_CIPHERSUITES_QUERY = `
query (
    $reportId: ID!,
    $onlyRecommended: Boolean,
    $onlyUsed: Boolean,
    $sorters: [CiphersuitesSorter]
  ) {
  node(id: $reportId) {
    ... on ReportDone {
      ciphersuites(
        sorters: $sorters,
        onlyRecommended: $onlyRecommended,
        onlyUsed: $onlyUsed
      ) {
        nid
        name
        recommended
        reference
        version
        hashAlg
        kex
        auth
        algorithmSym
        mac
        modeOfOperation
      }
    }
  }
}
`;

export const GET_CIPHERSUITES_FILTER_QUERY = `
query ($reportId: ID!) {
  node(id: $reportId) {
    ... on ReportDone {
      selected: ciphersuites(sorters: [{field: name, order: ASC}], onlyUsed: true) {
        nid
        name
      }
      supported: ciphersuites(sorters: [{field: name, order: ASC}], onlyUsed: false) {
        nid
        name
      }
    }
  }
}
`;

type GetListArgs = { reportId: number; onlyRecommended: boolean; onlyUsed: boolean; sorters: GQLSorterSet[] };

export type CiphersuiteFilters = {
  selected: Option[];
  supported: Option[];
};

export class ReportCiphersuitesRepository extends BaseGQLService {
  private readonly mapper = new ReportCiphersuiteMapper();

  async getList({ reportId, onlyRecommended, onlyUsed, sorters }: GetListArgs): Promise<Ciphersuite[]> {
    return this.query(
      GET_REPORT_CIPHERSUITES_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        onlyRecommended,
        onlyUsed,
        sorters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        return data.node.ciphersuites.map((ciphersuite: unknown) => this.mapper.mapGQLModel(ciphersuite));
      }
    );
  }

  async getForFilters(reportId: number): Promise<CiphersuiteFilters> {
    return this.query(
      GET_CIPHERSUITES_FILTER_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        return {
          selected: data.node.selected.map((s: any) => <Option>{ value: s.nid.toString(), label: s.name }),
          supported: data.node.supported.map((s: any) => <Option>{ value: s.nid.toString(), label: s.name }),
        };
      }
    );
  }
}
